import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { ChangeEvent, useEffect } from 'react'
import { Field, useFormikContext } from 'formik'
import {
  AddressFields,
  DebouncedTextField,
  MuiPhoneFieldFormik,
} from '@user/components/Formik'
import { ILocationType } from '@user/store/locationTypes/typings'
import { ILeadFormModel, ILeadLocationInfo } from '../../typings'
import { LocationLink } from '@user/components/Location'

import Grid from '@mui/material/Grid'
import { IDivision } from '@user/store/divisions/typings'
import type { FieldSettingsMap } from '@user/store/entityFormSettings'
import {
  addLeadDivisionId,
  addLeadFieldNameLabels,
  addLeadLocationTypeId,
  billToFieldNamePrefix,
} from '@user/components/Settings/EntityFormSettings'
import type { NameRequirementMode } from '@user/components/Settings/EntityFormSettings/config/nameRequirementMode'
import { TextFieldProps } from '@mui/material/TextField'
import { EmailCollection } from '@user/components/EmailCollection'
import { useData, useHandlers } from './hooks'
import Button from '@mui/material/Button'
import { FieldNames } from './FieldNames'
import { defaultFieldNames } from './typings'
import DeleteIcon from '@mui/icons-material/Delete'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import LocationTypeSelect from '../../FormFields/LocationTypeSelect/LocationTypeSelect'
import DivisionSelect from '../../FormFields/DivisionSelect/DivisionSelect'
import { getTouchedObject } from '@sales-center/common'
import { Measure } from '@user/components/DetailPages/sections/EntityName/MeasureAI/Measure'
import useMediaQuery from '@mui/material/useMediaQuery'
import { usePropertyDetailsEnabled } from '@user/components/DetailPages/sections/EntityName/MeasureAI/hooks/usePropertyDetailsEnabled'
import { PlusIcon } from '@sales-center/components'

interface Props {
  locationTypes: ILocationType[]
  disabled?: boolean
  leadLocationInfo?: ILeadLocationInfo
  divisions: IDivision[]
  hideOptional: boolean
  settingsMap: FieldSettingsMap
  textFieldProps?: TextFieldProps
  spacing?: number
  maxEmails?: number
  fieldNames?: FieldNames
  fieldNamePrefix?: string
  externalLocationId?: string
  fromMapView?: boolean
  nameRequirementMode: NameRequirementMode
}

export function LocationInformation(props: Props) {
  const {
    locationTypes,
    disabled,
    leadLocationInfo,
    divisions,
    settingsMap,
    textFieldProps,
    maxEmails = 3,
    hideOptional,
    spacing = 2,
    fieldNames = defaultFieldNames,
    fieldNamePrefix = '',
    externalLocationId = '',
    fromMapView = false,
    nameRequirementMode,
  } = props
  const tidalThemeActive = useTidalThemeActive()

  const {
    setFieldValue,
    setFieldTouched,
    setTouched,
    values,
    touched,
    errors,
    submitCount,
  } = useFormikContext<ILeadFormModel>()

  // Whether this instance's own group-level "or" validation error is active
  // (e.g. "First Name or Last Name is required") — shown once, at the
  // section level in MainForm, but the individual required-but-blank fields
  // should still visually flag as errored (red outline), just without
  // per-field "Required" text (that would duplicate the section message).
  const nameGroupHasError =
    !!errors[fieldNamePrefix + fieldNames.nameGroup] && submitCount > 0

  const {
    altPhoneExtDisabled,
    disableAddEmailButton,
    phoneExtDisabled,
    requiredAddress,
    requiredAltPhoneNumber,
    requiredCompany,
    requiredDivisionId,
    requiredEmail,
    requiredFirstName,
    requiredLastName,
    requiredLocationTypeId,
    requiredPhone,
    showAddress,
    showAltPhoneNumber,
    showCompany,
    showDivisionId,
    showEmail,
    showFirstName,
    showLastName,
    showLocationTypeId,
    showPhone,
  } = useData({
    settingsMap,
    maxEmails,
    fieldNames,
    fieldNamePrefix,
    disabled,
    nameRequirementMode,
  })

  const isMobile = useMediaQuery('(max-width:900px)')
  const measureAIEnabled = usePropertyDetailsEnabled()

  const {
    onBlurPhone,
    isMobilePhone,
    isMobileAltPhone,
    onAddEmailClick,
    onBlurAddress,
    onSelectAddress,
  } = useHandlers({ fieldNames, fieldNamePrefix })

  useEffect(() => {
    if (
      !values[fieldNamePrefix + fieldNames.locationTypeId] &&
      requiredLocationTypeId &&
      showLocationTypeId &&
      locationTypes?.length == 1
    ) {
      setFieldValue(
        fieldNamePrefix + fieldNames.locationTypeId,
        locationTypes[0]?.id,
      )
      setFieldValue(fieldNamePrefix + 'locationType', locationTypes[0]?.name)
    }
  }, [
    setFieldValue,
    requiredLocationTypeId,
    showLocationTypeId,
    values[fieldNamePrefix + fieldNames.locationTypeId],
  ])

  useEffect(() => {
    if (values.createNewLocation) {
      const isShowingErrors = Object.keys(errors).some(
        (key) =>
          touched[key as keyof typeof touched] &&
          errors[key as keyof typeof errors],
      )

      if (isShowingErrors) {
        setTouched(getTouchedObject(errors))
      }
    }
  }, [setTouched, getTouchedObject, values, errors])

  useEffect(() => {
    if (
      !values[fieldNamePrefix + fieldNames.divisionId] &&
      requiredDivisionId &&
      showDivisionId &&
      divisions?.length == 1
    ) {
      setFieldValue(fieldNamePrefix + fieldNames.divisionId, divisions[0]?.id)
      setFieldValue(fieldNamePrefix + 'division', divisions[0]?.name)
    }
  }, [
    setFieldValue,
    requiredDivisionId,
    showDivisionId,
    values[fieldNamePrefix + fieldNames.divisionId],
  ])

  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'
  const theme = useTheme()
  return (
    <Grid container spacing={spacing}>
      {leadLocationInfo?.externalLocationId &&
        leadLocationInfo?.externalLocationCode && (
          <Grid container item>
            <LocationLink
              externalLocationId={leadLocationInfo.externalLocationId}
              externalLocationCode={leadLocationInfo.externalLocationCode}
            />
          </Grid>
        )}
      <Grid container item spacing={1} direction="row">
        {showFirstName && (
          <Grid item xs md>
            <Field
              name={fieldNamePrefix + fieldNames.firstName}
              label="First Name"
              component={DebouncedTextField}
              required={!externalLocationId && requiredFirstName}
              forceError={requiredFirstName && nameGroupHasError}
              inputProps={{ maxLength: 15 }}
              avoidValidateOnBlur
              {...textFieldProps}
              data-test-id={`${fieldNamePrefix}-first-name`}
            />
          </Grid>
        )}

        {showLastName && (
          <Grid item xs md sx={{ paddingLeft: theme.spacing(2) }}>
            <Field
              name={fieldNamePrefix + fieldNames.lastName}
              label="Last Name"
              component={DebouncedTextField}
              required={!externalLocationId && requiredLastName}
              forceError={requiredLastName && nameGroupHasError}
              inputProps={{ maxLength: 20 }}
              avoidValidateOnBlur
              {...textFieldProps}
              data-test-id={`${fieldNamePrefix}-last-name`}
            />
          </Grid>
        )}
      </Grid>
      {showCompany && (
        <Grid item xs={12}>
          <Field
            name={fieldNamePrefix + fieldNames.company}
            label="Company Name"
            component={DebouncedTextField}
            required={!externalLocationId && requiredCompany}
            forceError={requiredCompany && nameGroupHasError}
            inputProps={{ maxLength: 35 }}
            avoidValidateOnBlur
            {...textFieldProps}
            data-test-id={`${fieldNamePrefix}-company-name`}
          />
        </Grid>
      )}
      {(showAddress || values.createNewLocation) && (
        <>
          <Grid item xs={12}>
            <AddressFields
              fieldNamePrefix={fieldNamePrefix}
              disabled={disabled}
              required={
                (!externalLocationId && requiredAddress) ||
                values.createNewLocation
              }
              textFieldProps={textFieldProps}
              spacing={spacing}
              setFieldTouched={setFieldTouched}
              avoidValidateOnBlur={true}
              onAddressBlur={onBlurAddress}
              onSelect={onSelectAddress}
            />
          </Grid>

          {measureAIEnabled &&
            (isMobile || fromMapView) &&
            values.fullAddress &&
            fieldNamePrefix !== billToFieldNamePrefix && (
              <Grid item xs={12}>
                <Measure address={values.fullAddress} variant="location" />
              </Grid>
            )}
        </>
      )}
      {showPhone && (
        <Grid container item spacing={spacing}>
          <Grid item xs={8}>
            <Field
              name={fieldNamePrefix + fieldNames.phone}
              label="Phone"
              component={MuiPhoneFieldFormik}
              margin="none"
              required={!externalLocationId && requiredPhone}
              onBlur={onBlurPhone}
              helperText={isMobilePhone ? 'Number entered is Mobile' : ''}
              {...textFieldProps}
              avoidValidateOnBlur
              data-test-id={`${fieldNamePrefix}-phone-number`}
            />
          </Grid>

          {!hideOptional && showPhone && (
            <Grid item xs={4}>
              <Field
                name={fieldNamePrefix + fieldNames.phoneExt}
                label="Ext"
                component={DebouncedTextField}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched(fieldNamePrefix + fieldNames.phoneExt, true)
                  setFieldValue(fieldNamePrefix + fieldNames.phoneExt, value)
                }}
                disabled={phoneExtDisabled}
                {...textFieldProps}
                inputProps={{ maxLength: 5 }}
                avoidValidateOnBlur
                data-test-id={`${fieldNamePrefix}-phone-ext-number`}
              />
            </Grid>
          )}
        </Grid>
      )}
      {showAltPhoneNumber && (
        <Grid container item spacing={spacing}>
          <Grid item xs={8}>
            <Field
              name={fieldNamePrefix + fieldNames.altPhone}
              label="Alternate Phone"
              component={MuiPhoneFieldFormik}
              margin="none"
              onBlur={onBlurPhone}
              helperText={isMobileAltPhone ? 'Number entered is Mobile' : ''}
              required={!externalLocationId && requiredAltPhoneNumber}
              {...textFieldProps}
              avoidValidateOnBlur
              data-test-id={`${fieldNamePrefix}-alt-phone-number`}
            />
          </Grid>

          {!hideOptional && showAltPhoneNumber && (
            <Grid item xs={4}>
              <Field
                name={fieldNamePrefix + fieldNames.altPhoneExt}
                label="Ext"
                component={DebouncedTextField}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  const value = e.target.value
                  setFieldTouched(
                    fieldNamePrefix + fieldNames.altPhoneExt,
                    true,
                  )
                  setFieldValue(fieldNamePrefix + fieldNames.altPhoneExt, value)
                }}
                disabled={altPhoneExtDisabled}
                {...textFieldProps}
                inputProps={{ maxLength: 5 }}
                avoidValidateOnBlur
                data-test-id={`${fieldNamePrefix}-alt-phone-ext-number`}
              />
            </Grid>
          )}
        </Grid>
      )}
      {showEmail && (
        <Grid container item spacing={spacing} pt={0}>
          <Grid item xs={12}>
            <EmailCollection<ILeadFormModel>
              name={fieldNamePrefix + fieldNames.emailAddresses}
              required={!externalLocationId && requiredEmail}
              singleModeEnabled={true}
              textFieldProps={textFieldProps}
              spacing={spacing}
              paddingTop={0}
              removeIcon={<DeleteIcon sx={{ color: '#AB1010' }} />}
              avoidValidateOnBlur
            />
            <Button
              disabled={disableAddEmailButton}
              variant="text"
              onClick={onAddEmailClick}
              sx={{
                paddingY: '6px',
                marginTop: theme.spacing(1),
                textTransform: 'none',
              }}
              data-test-id={`${fieldNamePrefix}-add-additional-email-button`}
              startIcon={<PlusIcon />}
            >
              Add additional email address
            </Button>
          </Grid>
        </Grid>
      )}
      {showLocationTypeId && (
        <Grid item xs={12}>
          <LocationTypeSelect
            name={fieldNamePrefix + fieldNames.locationTypeId}
            label={addLeadFieldNameLabels[addLeadLocationTypeId]}
            show={showLocationTypeId}
            required={!externalLocationId && requiredLocationTypeId}
            disabled={disabled}
            locationTypes={locationTypes}
            density={density}
          />
        </Grid>
      )}
      {/* division */}
      {showDivisionId && (
        <Grid item xs={12}>
          <DivisionSelect
            name={fieldNamePrefix + fieldNames.divisionId}
            label={addLeadFieldNameLabels[addLeadDivisionId]}
            required={!externalLocationId && requiredDivisionId}
            disabled={disabled}
            divisions={divisions}
            show={showDivisionId}
            density={density}
          />
        </Grid>
      )}
    </Grid>
  )
}
